import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { handleApiError } from "../../models/store/storeEffects";
import {
    fetchReportCostCenters,
    fetchReportCustomers,
    fetchReportEmployees,
    fetchReportSalaryRowTypes,
    fetchReportVehicles,
    fetchReportWorkTimeTypes,
    fetchReportEmployeeGroups,
    fetchReportSalaryPeriodTypes,
} from "../../store/reportOptionsSlice";
import { useAsyncThunkAction } from "../useAsyncThunkAction";
import { fetchCalendarDays } from "../../store/calendarSlice";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { SalaryRowTypeItem } from "../../models/salary/salaryRowTypeItem";
import {
    WorkTimeReportGridRow,
    WorkTimeReportRowDto,
    WorkTimeReportSalaryDto,
} from "../../models/reporting/workTimeReport";
import {
    getWorkTimeReportData,
    getWorkTimeReportSalaries,
} from "../../services/workHourSalaryService";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { showApiError } from "../../components/framework/formUtils";
import { SalaryPeriodTypeDto } from "../../models/salaryPeriodType/salaryPeriodType";
import { useFetch } from "../useFetch";
import { Base } from "../../framework/base";
import { useReload } from "../useReload";
import { SalaryPeriodDto } from "../../models/salaryPeriod/salaryPeriod";
import { useAppOptionValueEquals } from "../useAppOption";
import { OptionWorkTimeReportCalculationButtonEnabled } from "../../models/common/consts";
import _groupBy from "lodash/groupBy";

export interface WorkTimeReportData {
    loading: boolean;
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    workTimeReportData: WorkTimeReportGridRow[];
    salaryRowTypes: SalaryRowTypeItem[];
    salaryPeriodTypes: SalaryPeriodTypeDto[];
    employeeGroupCalculations: EmployeeGroupCalculation[];
    reload: () => void;
}

interface MissingPeriodsByEmployee {
    [id: string]: string[][];
}

interface EmployeesBySalaryPeriod {
    [id: string]: string[];
}

interface CalculateSalaryPeriodsByGroup {
    [id: string]: EmployeesBySalaryPeriod;
}

export interface SalaryPeriodCalculation {
    salaryPeriod: SalaryPeriodDto;
    employees: EmployeeListItemDto[];
}

export interface EmployeeGroupCalculation {
    employeeGroup: employeeGroupListItemDto;
    salaryPeriods: SalaryPeriodCalculation[];
}

export const useWorkTimeReportData = (): WorkTimeReportData => {
    const [workTimeReportDataLoading, setWorkTimeReportDataLoading] =
        useState(false);
    const [workTimeReportData, setWorkTimeReportData] =
        useState<WorkTimeReportRowDto[]>();
    const [salariesLoading, setSalariesLoading] = useState(false);
    const [salaries, setSalaries] = useState<WorkTimeReportSalaryDto[]>();
    const [reloadKey, reload] = useReload();

    const [calcButtonEnabled] = useAppOptionValueEquals(
        OptionWorkTimeReportCalculationButtonEnabled,
        true
    );

    const dispatch = useAppDispatch();

    const timeRange = useAppSelector(
        (state) => state.reportTemplate.current.timeRange
    );
    const selectedEmployeeGroups = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedEmployeeGroups
    );

    const vehiclesById = useAppSelector(
        (state) => state.reportOptions.vehicle.itemById
    );
    const employees = useAppSelector((state) => state.reportOptions.employees);
    const employeeGroups = useAppSelector(
        (state) => state.reportOptions.employeeGroups
    );
    const salaryRowTypes = useAppSelector(
        (state) => state.reportOptions.salaryRowTypes
    );
    const costCenters = useAppSelector(
        (state) => state.reportOptions.costCenters
    );
    const customersById = useAppSelector(
        (state) => state.reportOptions.customer.itemById
    );
    const workTimeTypesById = useAppSelector(
        (state) => state.reportOptions.workTimeType.itemById
    );
    const salaryPeriodTypes = useAppSelector(
        (state) => state.reportOptions.salaryPeriodTypes
    );

    const selectedVehicles = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedEmployees
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedCustomers
    );
    const selectedWorkTimeTypes = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedWorkTimeTypes
    );

    const visibleEmployeeIds = useMemo(() => {
        const employeeFilterActive = selectedEmployees?.length > 0;
        const employeeGroupFilterActive = selectedEmployeeGroups?.length > 0;

        return employees
            ?.filter(
                (e) =>
                    (!employeeFilterActive && !employeeGroupFilterActive) ||
                    selectedEmployees?.includes(e.id) ||
                    (!employeeFilterActive &&
                        selectedEmployeeGroups.includes(e.salaryGroupId))
            )
            .map((e) => e.id);
    }, [selectedEmployees, selectedEmployeeGroups, employees]);

    const missingPeriodsByEmployeeId = useMemo(() => {
        if (
            salariesLoading ||
            !salaries ||
            !timeRange[0] ||
            !timeRange[1] ||
            !visibleEmployeeIds
        )
            return;

        const periods = visibleEmployeeIds
            .map((id) => {
                const upToDatePeriods = salaries
                    .filter(
                        // Non-provisional salaries are always up-to-date
                        (s) =>
                            s.employeeId === id &&
                            (!s.isProvisional || s.isUpToDate)
                    )
                    .map((s) => [
                        s.salaryPeriodStartDate,
                        s.salaryPeriodEndDate,
                    ]);

                const missingDates = Base.getMissingDatesFromPeriods(
                    upToDatePeriods,
                    timeRange[0],
                    timeRange[1]
                );

                return [id, Base.getPeriodsFromDates(missingDates)];
            })
            .filter((o) => o[1].length > 0);

        return Object.fromEntries(periods) as MissingPeriodsByEmployee;
    }, [
        salariesLoading,
        salaries,
        visibleEmployeeIds,
        timeRange[0],
        timeRange[1],
    ]);

    const employeeGroupCalculations: EmployeeGroupCalculation[] =
        useMemo(() => {
            if (
                !employeeGroups ||
                !employees ||
                !salaryPeriodTypes ||
                !missingPeriodsByEmployeeId
            )
                return;

            const salaryPeriodsByGroup: CalculateSalaryPeriodsByGroup = {};
            const employeeGroupsById = Base.groupArray(employeeGroups, "id");
            const employeesById = Base.groupArray(employees, "id");
            const salaryPeriodsById: { [id: string]: SalaryPeriodDto } = {};

            Object.keys(missingPeriodsByEmployeeId).forEach((id) => {
                const employee = employeesById[id]?.[0];
                const group = employeeGroupsById[employee?.salaryGroupId]?.[0];
                const salaryPeriods = salaryPeriodTypes.find(
                    (t) => t.id === group?.salaryPeriodTypeId
                )?.salaryPeriods;

                if (!!salaryPeriods) {
                    const missingPeriods = missingPeriodsByEmployeeId[id];
                    const missingSalaryPeriods = salaryPeriods.filter((sp) =>
                        missingPeriods.some(
                            (p) => sp.startDate <= p[1] && sp.endDate >= p[0]
                        )
                    );

                    if (missingSalaryPeriods.length > 0) {
                        salaryPeriodsByGroup[group?.id] ??= {};

                        missingSalaryPeriods.forEach((sp) => {
                            salaryPeriodsByGroup[group?.id][sp.id] ??= [];
                            salaryPeriodsByGroup[group?.id][sp.id].push(id);
                            salaryPeriodsById[sp.id] ??= sp;
                        });
                    }
                }
            });

            return Object.keys(salaryPeriodsByGroup)
                .map((id) => {
                    const employeesByPeriod = salaryPeriodsByGroup[id];

                    return {
                        employeeGroup: employeeGroupsById[id]?.[0],
                        salaryPeriods: Object.keys(employeesByPeriod)
                            .map((salaryPeriodId) => {
                                const employeeIds =
                                    employeesByPeriod[salaryPeriodId];

                                return {
                                    salaryPeriod:
                                        salaryPeriodsById[salaryPeriodId],
                                    employees: employeeIds
                                        .map(
                                            (employeeId) =>
                                                employeesById[employeeId][0]
                                        )
                                        .sort((a, b) =>
                                            Base.stringCompare(
                                                a.fullName,
                                                b.fullName
                                            )
                                        ),
                                };
                            })
                            .sort((a, b) =>
                                Base.dayjsCompare(
                                    a.salaryPeriod.startDate,
                                    b.salaryPeriod.endDate
                                )
                            ),
                    };
                })
                .sort((a, b) =>
                    Base.stringCompare(
                        a.employeeGroup.name,
                        b.employeeGroup.name
                    )
                );
        }, [
            missingPeriodsByEmployeeId,
            employees,
            employeeGroups,
            salaryPeriodTypes,
        ]);

    const gridRows = useMemo(() => {
        const rowsByDateAndEmployeeId = _groupBy(
            (workTimeReportData ?? []).filter((d) => !!d.workShiftId),
            (d) => `${d.dateStr}_${d.employeeId}`
        );

        return workTimeReportData?.map((d) => {
            const vehicle = !!d.vehicleId ? vehiclesById?.[d.vehicleId] : null;
            let workShiftId = d.workShiftId;
            let workShiftStartTime = d.workShiftStartTime;
            let workShiftEndTime = d.workShiftEndTime;

            if (!workShiftId) {
                // If row doesn't have a workShiftId,
                // first look up workShiftId for same day
                // to link dayBooking rows with first shift of the day.
                const row =
                    rowsByDateAndEmployeeId[
                        `${d.dateStr}_${d.employeeId}`
                    ]?.[0];

                if (!!row) {
                    workShiftId = row.workShiftId;
                    workShiftStartTime = row.workShiftStartTime;
                    workShiftEndTime = row.workShiftEndTime;
                } else {
                    // if shift is not found, use dateStr so different day rows without shift will not be
                    // grouped to together.
                    workShiftId = d.dateStr;
                }
            }

            const workTimeTypeName = !!d.workTimeTypeId
                ? workTimeTypesById?.[d.workTimeTypeId]?.name
                : null;

            const customerName = !!d.customerId
                ? customersById?.[d.customerId]?.name
                : null;

            return {
                ...d,
                workShiftId,
                workShiftStartTime,
                workShiftEndTime,
                employeeName: employees?.find((e) => e.id === d.employeeId)
                    ?.fullName,
                costCenterName: costCenters?.find(
                    (e) => e.id === d.costCenterId
                )?.name,
                customerName,
                vehicleName: !!vehicle
                    ? `${vehicle.registerNumber} - ${vehicle.brand}`
                    : null,
                workTimeTypeName,
                salaryRows: d.salaryRows.map((sr) => ({
                    ...sr,
                    salaryRowTypeName: salaryRowTypes?.find(
                        (t) => t.id === sr.salaryRowTypeId
                    )?.name,
                })),
                totals: d.salaryRows.reduce(
                    (acc, curr) => {
                        if (!!curr.salaryRowTypeId) {
                            acc[`srt_${curr.salaryRowTypeId}`] =
                                (acc[`srt_${curr.salaryRowTypeId}`] ?? 0) +
                                curr.amount;
                        }

                        acc[`cat_${curr.category}`] ??= 0;
                        acc[`cat_${curr.category}`] += curr.amount;
                        return acc;
                    },
                    {
                        [`wtt_${workTimeTypeName ?? "null"}`]: d.duration,
                        [`e_${d.employeeId ?? "null"}`]: d.duration,
                        [`cc_${d.costCenterId ?? "null"}`]: d.duration,
                        [`c_${customerName ?? "null"}`]: d.duration,
                        [`v_${vehicle?.registerNumber ?? "null"}`]: d.duration,
                        duration: d.duration,
                    }
                ),
            } as WorkTimeReportGridRow;
        });
    }, [
        workTimeReportData,
        salaryRowTypes,
        employees,
        vehiclesById,
        costCenters,
        customersById,
        workTimeTypesById,
    ]);

    const loadWorkTimeReportData = useCallback(
        (signal?: AbortSignal) => {
            if (!timeRange[0] || !timeRange[1]) return;

            setWorkTimeReportDataLoading(true);

            getWorkTimeReportData(
                {
                    employeeGroupIds: selectedEmployeeGroups,
                    employeeIds: selectedEmployees,
                    startDate: timeRange[0],
                    endDate: timeRange[1],
                    vehicleIds: selectedVehicles,
                    customerIds: selectedCustomers,
                    costCenterIds: selectedCostCenters,
                    workTimeTypeIds: selectedWorkTimeTypes,
                },
                signal
            )
                .then((res) => setWorkTimeReportData(res?.data ?? []))
                .catch((err) => {
                    if (!signal?.aborted) {
                        handleApiError(err, dispatch);
                        console.log("error", err);
                    }
                })
                .finally(() => setWorkTimeReportDataLoading(false));
        },
        [
            // Prevent data reloading when template reference is updated but value remains the same.
            selectedEmployeeGroups?.map((id) => id ?? "null").join(".") ?? "",
            selectedEmployees?.map((id) => id ?? "null").join(".") ?? "",
            timeRange[0],
            timeRange[1],
            selectedVehicles?.map((id) => id ?? "null").join(".") ?? "",
            selectedCustomers?.map((id) => id ?? "null").join(".") ?? "",
            selectedCostCenters?.map((id) => id ?? "null").join(".") ?? "",
            selectedWorkTimeTypes?.map((id) => id ?? "null").join(".") ?? "",
            reloadKey,
        ]
    );

    const loadSalaries = useCallback(
        (signal?: AbortSignal) => {
            // Unnecessary to load this data if calc button is disabled.
            if (!timeRange[0] || !timeRange[1] || !calcButtonEnabled) return;

            setSalariesLoading(true);

            getWorkTimeReportSalaries(
                {
                    employeeGroupIds: selectedEmployeeGroups,
                    employeeIds: selectedEmployees,
                    startDate: timeRange[0],
                    endDate: timeRange[1],
                },
                signal
            )
                .then((res) => setSalaries(res?.data ?? []))
                .catch((err) => {
                    if (!signal?.aborted) {
                        handleApiError(err, dispatch);
                        console.log("error", err);
                    }
                })
                .finally(() => setSalariesLoading(false));
        },
        [
            selectedEmployeeGroups?.join(".") ?? "",
            selectedEmployees?.join(".") ?? "",
            timeRange[0],
            timeRange[1],
            reloadKey,
            calcButtonEnabled,
        ]
    );

    useFetch(
        (signal) => void loadWorkTimeReportData(signal),
        {
            withLoadingIndicator: false,
        },
        [loadWorkTimeReportData]
    );

    useFetch(
        (signal) => void loadSalaries(signal),
        {
            withLoadingIndicator: false,
        },
        [loadSalaries]
    );

    useAsyncThunkAction(
        () => fetchReportEmployeeGroups(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportVehicles(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportEmployees(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportSalaryRowTypes(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportCostCenters(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportCustomers(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportWorkTimeTypes(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportSalaryPeriodTypes(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => {
            if (!timeRange[0] || !timeRange[1]) {
                return;
            }
            return fetchCalendarDays({
                startDate: timeRange[0],
                endDate: timeRange[1],
            });
        },
        { onError: () => null },
        [timeRange[0], timeRange[1]]
    );

    return {
        loading: workTimeReportDataLoading,
        employees,
        employeeGroups,
        workTimeReportData: gridRows,
        salaryRowTypes,
        salaryPeriodTypes,
        employeeGroupCalculations,
        reload,
    };
};
